import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { listFactories } from '../../services/factories';
import { listWorkshops } from '../../services/workshops';
import { listSections } from '../../services/sections';
import { listRooms } from '../../services/rooms';
import { listCameras } from '../../services/cameras';

/*
 * Опции локальных фильтров таблиц (ТЗ v4 §24–26: «checkbox filters
 * Object/Location/Camera; cascading options»).
 *
 * Уровни таблиц — Объект / Локация / Камера, то есть срез полной иерархии.
 * Локации собираются обходом выбранных объектов (подразделение → область →
 * локация); промежуточные уровни пользователю здесь не показываются, но
 * запрашиваются, потому что только так у локации есть родительский объект —
 * без него две одноимённые локации неразличимы (ТЗ §25).
 *
 * ACL обеспечивает backend: во всех списках приходит только доступное.
 * Глобальный scope страницы дополнительно сужает опции — локальный фильтр не
 * может показать локацию вне выбранной области данных (ТЗ §25 DoD).
 *
 * Запросы идут только когда панель фильтров открыта: закрытая панель не должна
 * стоить пользователю обхода дерева.
 */

const normalizeList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

const IDLE = { options: [], state: 'idle' };

const allowedBy = (scopeIds) => {
  const allowed = new Set((scopeIds || []).map(String));
  return allowed.size === 0 ? null : allowed;
};

/** Один уровень: загрузка по требованию, повтор по кнопке, partial при частичном отказе. */
const useLazyLevel = (loader, { enabled, key }) => {
  const [level, setLevel] = useState(IDLE);
  const [token, setToken] = useState(0);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    if (!enabled) {
      setLevel(IDLE);
      return undefined;
    }
    let cancelled = false;
    setLevel((prev) => ({ options: prev.options, state: 'loading' }));
    Promise.resolve()
      .then(() => loaderRef.current())
      .then((result) => {
        if (cancelled) return;
        const options = result?.options || [];
        setLevel({ options, state: options.length ? 'ready' : 'empty', partial: result?.partial });
      })
      .catch(() => { if (!cancelled) setLevel({ options: [], state: 'error' }); });
    return () => { cancelled = true; };
    // key описывает весь вход загрузчика значением, а не ссылкой.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key, token]);

  return { ...level, reload: useCallback(() => setToken((value) => value + 1), []) };
};

const settled = async (promises) => {
  const results = await Promise.all(promises.map((promise) => promise.then(
    (value) => ({ ok: true, value }),
    () => ({ ok: false })
  )));
  return {
    values: results.filter((result) => result.ok).map((result) => result.value),
    failed: results.filter((result) => !result.ok).length,
    total: results.length,
  };
};

export const useTableFilterOptions = ({ state, scope, enabled = false }) => {
  const objectIds = useMemo(() => (state?.objectIds || []).map(String), [state]);
  const locationIds = useMemo(() => (state?.locationIds || []).map(String), [state]);
  const scopeKey = JSON.stringify({
    factoryIds: scope?.factoryIds || [],
    roomIds: scope?.roomIds || [],
    cameraIds: scope?.cameraIds || [],
  });

  const objects = useLazyLevel(
    async () => {
      const allowed = allowedBy(JSON.parse(scopeKey).factoryIds);
      const items = normalizeList(await listFactories());
      return {
        options: items
          .map((item) => ({ id: String(item.id), name: item.name || `#${item.id}`, parentName: '' }))
          .filter((option) => !allowed || allowed.has(option.id)),
      };
    },
    { enabled, key: `objects|${scopeKey}` }
  );

  const objectNames = useMemo(
    () => Object.fromEntries(objects.options.map((option) => [option.id, option.name])),
    [objects.options]
  );
  const objectNamesKey = JSON.stringify(objectNames);

  const locations = useLazyLevel(
    async () => {
      const allowed = allowedBy(JSON.parse(scopeKey).roomIds);
      const names = JSON.parse(objectNamesKey);
      let failed = 0;
      const perObject = await settled(objectIds.map(async (objectId) => {
        const workshops = normalizeList(await listWorkshops(objectId));
        const sections = await settled(workshops.map((workshop) => listSections(workshop.id)));
        failed += sections.failed;
        const sectionIds = sections.values.flatMap((data) => normalizeList(data)).map((item) => item.id);
        const rooms = await settled(sectionIds.map((sectionId) => listRooms(sectionId)));
        failed += rooms.failed;
        return rooms.values.flatMap((data) => normalizeList(data).map((room) => ({
          id: String(room.id),
          name: room.name || `#${room.id}`,
          parentId: objectId,
          parentName: names[objectId] || '',
        })));
      }));
      const seen = new Set();
      return {
        options: perObject.values
          .flat()
          .filter((option) => {
            if (seen.has(option.id) || (allowed && !allowed.has(option.id))) return false;
            seen.add(option.id);
            return true;
          }),
        partial: failed + perObject.failed > 0,
      };
    },
    { enabled: enabled && objectIds.length > 0, key: `locations|${objectIds.join(',')}|${scopeKey}|${objectNamesKey}` }
  );

  const locationNames = useMemo(
    () => Object.fromEntries(locations.options.map((option) => [option.id, option.name])),
    [locations.options]
  );
  const locationNamesKey = JSON.stringify(locationNames);

  const cameras = useLazyLevel(
    async () => {
      const allowed = allowedBy(JSON.parse(scopeKey).cameraIds);
      const names = JSON.parse(locationNamesKey);
      const result = await settled(locationIds.map(async (locationId) => {
        const items = normalizeList(await listCameras(locationId));
        return items.map((camera) => ({
          id: String(camera.id),
          name: camera.name || `Камера #${camera.id}`,
          parentId: locationId,
          parentName: names[locationId] || '',
        }));
      }));
      const seen = new Set();
      return {
        options: result.values
          .flat()
          .filter((option) => {
            if (seen.has(option.id) || (allowed && !allowed.has(option.id))) return false;
            seen.add(option.id);
            return true;
          }),
        partial: result.failed > 0,
      };
    },
    { enabled: enabled && locationIds.length > 0, key: `cameras|${locationIds.join(',')}|${scopeKey}|${locationNamesKey}` }
  );

  return useMemo(() => ({
    objectIds: objects,
    locationIds: locations,
    cameraIds: cameras,
  }), [objects, locations, cameras]);
};

export default useTableFilterOptions;
